import React, { Fragment } from 'react';
import { Typography, Grid, Box, Card, CardContent } from '@mui/material';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';

import { t } from 'i18next';
import TextInput from './formElements/TextInput';
import TextFieldInput from './formElements/TextFieldInput';
import RadioInput from './formElements/RadioInput';
import CheckboxInput from './formElements/CheckboxInput';
import ScaleInput from './formElements/ScaleInput';
import TimeInput from './formElements/TimeInput';

const QuestionnaireViewer = ({ title, description, formData, onInputChange }) => {
    const theme = useTheme();

    const renderFormElement = (step, index) => {
        const answerType = step.answerFormat?.type || 'text';

        const texts = step.content?.filter((c) => c.type === 'text') || [];
        const markdowns = step.content?.filter((c) => c.type === 'markdown') || [];

        const sectionTitle = texts.length > 0 ? texts[0].text.trim() : '';
        const lastMarkdown = markdowns[markdowns.length - 1];
        const lastText = texts[texts.length - 1];

        const questionText =
            lastMarkdown?.text?.trim() ||
            (texts.length > 1 ? lastText?.text?.trim() : sectionTitle);

        const showSectionTitleOutsideCard = sectionTitle && sectionTitle !== questionText;

        const bodyTexts = texts
            .slice(1, texts.length - 1)
            .filter((t) => t.text && t.text.trim() !== questionText);

        const bodyMarkdowns = markdowns
            .slice(0, -1)
            .filter((m) => m.text && m.text.trim() !== questionText);

        const rawOptions = step.answerFormat?.textChoices || [];
        const options = rawOptions.map((opt) =>
            typeof opt === 'string'
                ? { value: opt, label: opt }
                : { value: opt.value, label: opt.text }
        );

        const questionnaireData = {
            id: step.id,
            questionNumber: index + 1,
            value: questionText,
            answer: step.answer,
            options,
            scaleType: step.answerFormat?.scaleType,
            radioCount: step.answerFormat?.radioCount || options.length,
            minimumValue: step.answerFormat?.minimumValue,
            maximumValue: step.answerFormat?.maximumValue,
            defaultValue: step.answerFormat?.defaultValue,
            minimumValueDescription: step.answerFormat?.minimumValueDescription,
            maximumValueDescription: step.answerFormat?.maximumValueDescription
        };

        return (
            <Box key={index} sx={{ mb: 4 }}>
                {showSectionTitleOutsideCard && (
                    <Typography variant="h6" sx={{ color: 'gray', mb: 1 }}>
                        {sectionTitle}
                    </Typography>
                )}

                <Card variant="outlined">
                    <CardContent>
                        {bodyTexts.map((txt, i) => (
                            <Typography key={`text-${i}`} variant="body2">
                                {txt.text}
                            </Typography>
                        ))}

                        {bodyMarkdowns.map((md, i) => (
                            <Typography key={`md-${i}`} variant="body2">
                                {md.text}
                            </Typography>
                        ))}

                        {(() => {
                            switch (answerType) {
                                case 'text':
                                    return <TextInput data={questionnaireData} />;
                                case 'textarea':
                                    return <TextFieldInput data={questionnaireData} />;
                                case 'single':
                                    return (
                                        <RadioInput
                                            questionnaireData={questionnaireData}
                                        />
                                    );
                                case 'multiple':
                                    return (
                                        <CheckboxInput
                                            questionnaireData={questionnaireData}
                                        />
                                    );
                                case 'scale':
                                    return (
                                        <ScaleInput
                                            questionnaireData={questionnaireData}
                                        />
                                    );
                                case 'time':
                                    return (
                                        <TimeInput
                                            questionnaireData={questionnaireData}
                                        />
                                    );
                                default:
                                    return (
                                        <Typography color="error">
                                            {t('questionnaires.unknown_type', {
                                                type: answerType
                                            })}
                                        </Typography>
                                    );
                            }
                        })()}
                    </CardContent>
                </Card>
            </Box>
        );
    };

    return (
        <Fragment>
            <Grid container spacing={2} direction="column">
                <Grid item xs={12}>
                    <Typography
                        variant="h1"
                        align="center"
                        gutterBottom
                        sx={{ color: theme.palette.primary.dark }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body1"
                        align="center"
                        gutterBottom
                        sx={{ fontSize: '20px' }}
                    >
                        {description}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    {formData.map((item, index) => renderFormElement(item, index))}
                </Grid>
            </Grid>
        </Fragment>
    );
};

QuestionnaireViewer.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    formData: PropTypes.array,
    onInputChange: PropTypes.func
};

export default QuestionnaireViewer;
